/**
 * Step-3 audience compiler — a pure-TS port of the legacy `__segToSql`.
 * Compiles the audience criteria into the WHERE clause run against
 * `Lead_Salesforce_1` (the SFMC synced Salesforce Lead DE).
 *
 * Field knowledge encoded here was verified LIVE against SFMC (2026-07-20,
 * ATL/JAX) — keep these rules:
 * - `division_code1__c` short codes work; `persona__c` is 100% blank — never
 *   use it; profile filters use `customer_profile__c` (36% populated).
 * - `move_in_time_frame__c` picklist matches the pills (38% populated).
 * - "6 months" = 180 DAYS (an old digit-grab turned it into 6 days).
 * - Junk lead statuses (Duplicate, Void, Please Select) stay excluded because
 *   any selection compiles to `status IN (...)`.
 * - Always-on suppressions replicate the national report-template recipe:
 *   opt-out, do-not-email, do-not-contact, converted, registration state,
 *   blank/placeholder emails — null-tolerant (synced booleans can be blank).
 * - Opportunities go through Opportunity_Salesforce (`division_code__c`!) →
 *   OpportunityContactRole → Contact — that union lives in the worker's
 *   /sfmc-populate-de (includeOpps mode), not here.
 */

export interface GeoPick {
  /** "comm" | "mp" | "aoi" */
  t: string;
  v: string;
}

export interface AudienceCriteria {
  /** Step-1 fallbacks when no explicit geo includes are picked */
  community?: string;
  masterplan?: string;
  geoInclude?: GeoPick[];
  geoExclude?: GeoPick[];
  /** FTHB | ActiveAdult | MoveUp (Investor has no populated column — copy-only) */
  buyerProfile?: string;
  /** e.g. "Last 30 days" | "Last 90 days" | "Last 6 months" | "Last 2 years" */
  createdWindow?: string;
  /** rfi30 | rfi90 | rfi180 | norfi */
  engagement?: string;
  /** pill labels like "0–3 mo", "3–6 mo", "6–12 mo", "12+ mo" */
  buyerTimeframe?: string[];
  /** price-tier labels — resolved through priceTierSql/priceLabelMap */
  buyerPriceRange?: string[];
  priceFit?: boolean;
  suppressionWindows?: string[];
  mustHaveFeatures?: string[];
  productType?: string;
  /** lead-status labels — resolved through leadStatusSql/leadStatusLabelMap */
  leadStatus?: string[];
  websiteActive?: boolean;
}

export interface AudienceLookups {
  /** label → tier key */
  priceLabelMap?: Record<string, string>;
  /** tier key → desired_price_range__c values */
  priceTierSql?: Record<string, string[]>;
  /** label → status key */
  leadStatusLabelMap?: Record<string, string>;
  /** status key → status values (incl. legacy variants) */
  leadStatusSql?: Record<string, string[]>;
  /** community-name → core tokens (legacy commCore); default strips "60s - " prefixes */
  commCore?: (name: string) => string;
}

/* ══ Recommended Salesforce reports — verbatim port of the monolith's
   resolveAudience() (index.html L4423–4431) + personaKey (L4386) +
   stageFromMsg (L4385). Feeds autoSF's sfInclude auto-suggest. ══ */

export interface RecReport { name: string; tf: string; why: string }
export interface RecTier { tier: "primary" | "secondary" | "consider"; reports: RecReport[] }

/** message CODE → journey stage (monolith stageFromMsg; offer codes → Urgency) */
export function stageFromMsg(code: string): string {
  const m: Record<string, string> = {
    CS: "Tease", ComOv: "Tease", GO: "Launch", PhR: "Launch", FtFp: "Sustain", QMI: "Sustain",
    HOTW: "Sustain", Tour: "Sustain", Ei: "Sustain", OC: "Sustain", NG: "Sustain", "55": "Sustain",
    Ref: "Sustain", Invstr: "Sustain", FO: "LastChance", SEvt: "Events", Web: "Events", SwGv: "Events",
  };
  if (["FxdRate", "321BD", "21BD", "1BD", "76ARM", "CC", "FC", "MthPay", "Opts"].includes(code)) return "Urgency";
  return m[code] || "Sustain";
}

/** monolith personaKey(): the UTM Targeting option's persona flag, else Market */
export function personaKeyFromUtm(utmTarget?: string): string {
  const P: Record<string, string> = { ActiveAdult: "ActiveAdult", FTHB: "FTHB", MoveUp: "MoveUp", National: "National", Investor: "Investor" };
  return P[utmTarget || ""] || "Market";
}

/** monolith PERSONA[..].list — the report-name persona token (null = none) */
const PERSONA_LIST: Record<string, string | null> = {
  FTHB: "FTHB", MoveUp: "Move Up", ActiveAdult: "AA",
  Investor: null, Community: null, Market: null, National: null,
};

export function resolveAudience(args: { messageCode?: string; audType?: string; community?: string; aoi?: string; utmTarget?: string }): { tiers: RecTier[] } {
  const stage = stageFromMsg(args.messageCode || "");
  const at = args.audType || "LO";
  const comm = args.community || null;
  const aoi = args.aoi || "";
  const list = PERSONA_LIST[personaKeyFromUtm(args.utmTarget)] || null;
  const tiers: RecTier[] = [];
  const R = (name: string, tf: string, why: string): RecReport => ({ name, tf, why });
  const realtor = at === "R" || at === "LOR";
  if (stage === "Tease") {
    const pr: RecReport[] = [];
    if (aoi) pr.push(R("DIVCode Area of Interest Leads", "3Y", `AOI = ${aoi}. Area-level before the community exists in SF.`));
    else pr.push(R("DIVCode Market City Code Leads 3Y", "3Y", "Market-level tease."));
    if (list) pr.push(R(`DIVCode Customer Profile — ${list} Leads 3Y`, "3Y", "Targeting narrows the pool."));
    tiers.push({ tier: "primary", reports: pr });
    tiers.push({ tier: "secondary", reports: [R("DIVCode No Community Leads 3Y", "3Y", "Not yet assigned to a community.")] });
    tiers.push({ tier: "consider", reports: [R("DIVCode All Leads Last 3Y", "3Y", "If the area list is too small.")] });
  } else if (stage === "Launch") {
    tiers.push({ tier: "primary", reports: [R("DIVCode Community Leads with IH 3Y", "3Y", `Interaction history on ${comm || "this community"}.`)] });
    const sec = [R("DIVCode Community Leads 3Y", "3Y", "Broader launch-day reach.")];
    if (["LO", "O", "LOR"].includes(at)) sec.push(R("DIVCode Community ContactswOpps 7Y", "7Y", "Adds opps, surfaced first."));
    tiers.push({ tier: "secondary", reports: sec });
  } else if (stage === "Sustain") {
    tiers.push({ tier: "primary", reports: [R("DIVCode Community Leads with IH 3Y", "3Y", "Engaged community leads.")] });
    const sec: RecReport[] = [];
    if (list) sec.push(R(`DIVCode Customer Profile — ${list} Leads 3Y`, "3Y", "Persona match."));
    if (["LO", "O", "LOR"].includes(at)) sec.push(R("DIVCode Community ContactswOpps 7Y", "7Y", "Keeps pipeline warm."));
    if (!sec.length) sec.push(R("DIVCode Community Leads 3Y", "3Y", "Broader reach."));
    tiers.push({ tier: "secondary", reports: sec });
  } else if (stage === "Urgency") {
    tiers.push({ tier: "primary", reports: [R("DIVCode Community ContactswOpps 7Y", "7Y", "Opps convert first."), R("DIVCode Community Leads with IH 3Y", "3Y", "Engaged leads likely to act.")] });
    tiers.push({ tier: "consider", reports: [R("DIVCode Community Leads 3Y", "3Y", "Widen only if needed.")] });
  } else if (stage === "LastChance") {
    tiers.push({ tier: "primary", reports: [R("DIVCode Community Leads 3Y", "3Y", "Widest net.")] });
    tiers.push({ tier: "secondary", reports: [R("DIVCode Community ContactswOpps 7Y", "7Y", "Opps first.")] });
    tiers.push({ tier: "consider", reports: [R("DIVCode All Leads Last 3Y", "3Y", "Maximum coverage.")] });
  } else {
    const pr = [R("DIVCode Community Leads with IH 3Y", "3Y", "Local, engaged leads.")];
    if (list) pr.push(R(`DIVCode Customer Profile — ${list} Leads 3Y`, "3Y", "Event persona match."));
    tiers.push({ tier: "primary", reports: pr });
    tiers.push({ tier: "consider", reports: [R("DIVCode Area of Interest Leads", "3Y", "Widen to local area.")] });
  }
  if (realtor) {
    const rr = [R("DIVCode All Realtors: Contact", "No TF", "Broker contacts."), R("DIVCode All Realtors: Leads", "No TF", "Realtor leads.")];
    const ex = tiers.find((x) => x.tier === "secondary");
    if (ex) ex.reports.push(...rr);
    else tiers.push({ tier: "secondary", reports: rr });
  }
  return { tiers };
}

const q = (s: string) => String(s).replace(/'/g, "''");
const defaultCommCore = (name: string) => String(name).replace(/^\s*\d{1,3}'?\s*s?\s*[-–—]\s*/i, "").trim();

export function segToSql(divisionCode: string, F: AudienceCriteria, lookups: AudienceLookups = {}, opts: { noSup?: boolean } = {}): string {
  const commCore = lookups.commCore || defaultCommCore;
  const C: string[] = ["division_code1__c = '" + String(divisionCode).replace(/'/g, "") + "'"];

  /* GEOGRAPHY — includes OR-grouped, excludes AND NOT'ed; Step-1 fallback otherwise */
  const geoPred = (x: GeoPick) => {
    const core = q(commCore(x.v) || x.v);
    if (x.t === "aoi") return "area_of_interest__c LIKE '%" + q(x.v) + "%'";
    return "proper_community_name__c LIKE '%" + core + "%'";
  };
  const gInc = F.geoInclude || [];
  const gExc = F.geoExclude || [];
  if (gInc.length) C.push("(" + gInc.map(geoPred).join(" OR ") + ")");
  else {
    const comm = F.community;
    if (comm && !/market-level/i.test(comm)) {
      const core = q(commCore(comm));
      if (core) C.push("proper_community_name__c LIKE '%" + core + "%'");
    } else if (F.masterplan && !/select/i.test(F.masterplan)) {
      const mpc = q(commCore(F.masterplan));
      if (mpc) C.push("proper_community_name__c LIKE '%" + mpc + "%'");
    }
  }
  gExc.forEach((x) => C.push("NOT (" + geoPred(x) + ")"));

  /* Customer profile — customer_profile__c only */
  const profLike = ({ FTHB: "First-Time Homebuyer", ActiveAdult: "55-Plus", MoveUp: "Move-up Buyer" } as Record<string, string>)[F.buyerProfile || ""];
  if (profLike) C.push("customer_profile__c LIKE '%" + profLike + "%'");

  /* created-window mapping — explicit day counts */
  const cw = String(F.createdWindow || "");
  if (/30 days/i.test(cw)) C.push("createddate >= DATEADD(day, -30, GETDATE())");
  else if (/90 days/i.test(cw)) C.push("createddate >= DATEADD(day, -90, GETDATE())");
  else if (/6 months/i.test(cw)) C.push("createddate >= DATEADD(day, -180, GETDATE())");
  else if (/2 years/i.test(cw)) C.push("createddate >= DATEADD(day, -730, GETDATE())");

  /* ENGAGEMENT — Interaction_History = a new RFI/inquiry; match lead id OR email (robust) */
  const eng = String(F.engagement || "");
  if (eng) {
    const IH = "[Interaction_History__c_Salesforce]";
    const ihIn = (days: number) =>
      "(id IN (SELECT ih.Lead__c FROM " + IH + " ih WHERE ih.Interaction_Date__c >= DATEADD(day,-" + days + ",GETDATE())) OR email IN (SELECT ih2.Email__c FROM " + IH + " ih2 WHERE ih2.Interaction_Date__c >= DATEADD(day,-" + days + ",GETDATE())))";
    if (eng === "rfi30") C.push(ihIn(30));
    else if (eng === "rfi90") C.push(ihIn(90));
    else if (eng === "rfi180") C.push(ihIn(180));
    else if (eng === "norfi")
      C.push("id NOT IN (SELECT ih.Lead__c FROM " + IH + " ih WHERE ih.Interaction_Date__c >= DATEADD(day,-180,GETDATE())) AND email NOT IN (SELECT ih2.Email__c FROM " + IH + " ih2 WHERE ih2.Interaction_Date__c >= DATEADD(day,-180,GETDATE()))");
  }

  /* Buyer timeframe — compiles only when the user NARROWS (proper subset) */
  const tf = F.buyerTimeframe || [];
  if (tf.length && tf.length < 4) {
    const MTF: Record<string, string> = { "0": "0-3 months", "3": "3-6 months", "6": "6-12 months", "12": "greater than 12 months" };
    let mv: string[] = [];
    tf.forEach((l) => {
      const k = (String(l).match(/^(\d+)/) || [])[1];
      if (k && MTF[k]) mv.push(MTF[k]);
      if (/12\+/.test(l)) mv.push(MTF["12"]);
    });
    mv = [...new Set(mv)];
    if (mv.length) C.push("move_in_time_frame__c IN (" + mv.map((v) => "'" + v + "'").join(",") + ")");
  }

  /* Price range — tier labels resolve through the live-probed tier map */
  const bpr = F.buyerPriceRange || [];
  if (bpr.length && lookups.priceTierSql && lookups.priceLabelMap) {
    const pv: string[] = [];
    bpr.forEach((lb) => {
      const k = lookups.priceLabelMap![lb] || lb;
      (lookups.priceTierSql![k] || []).forEach((v) => pv.push("'" + q(v) + "'"));
    });
    if (pv.length) C.push("desired_price_range__c IN (" + pv.join(",") + ")");
  } else if (F.priceFit) C.push("desired_price_range__c IS NOT NULL");

  /* Optional suppression windows */
  const sw = F.suppressionWindows || [];
  if (!opts.noSup && sw.indexOf("Emailed in last 3 days") > -1)
    C.push("id NOT IN (SELECT s3.SubscriberKey FROM _Sent s3 WHERE s3.EventDate >= DATEADD(DAY,-3,GETDATE()))");
  if (!opts.noSup && sw.indexOf("Affiliate opt-outs") > -1)
    C.push("(do_not_allow_affiliate_marketing__c IS NULL OR do_not_allow_affiliate_marketing__c NOT IN ('true','True','1'))");

  (F.mustHaveFeatures || []).forEach((f) => C.push("desired_home_features__c LIKE '%" + q(f) + "%'"));

  const prod = F.productType;
  if (prod && !/any/i.test(prod)) {
    const pw = String(prod).replace(/\(.*\)/, "").replace(/'/g, "''").trim().split(/[\s-]/)[0];
    if (pw) C.push("desired_home_type__c LIKE '%" + pw + "%'");
  }

  /* lead status — real values; any selection also keeps junk statuses out */
  const lsSel = F.leadStatus || [];
  if (lsSel.length && lookups.leadStatusSql && lookups.leadStatusLabelMap) {
    const vals: string[] = [];
    lsSel.forEach((lb) => {
      const k = lookups.leadStatusLabelMap![lb];
      (lookups.leadStatusSql![k] || []).forEach((v) => vals.push(v));
    });
    if (vals.length) C.push("status IN (" + vals.map((v) => "'" + q(v) + "'").join(",") + ")");
  }

  if (F.websiteActive) C.push("visit_date__c >= DATEADD(day, -30, GETDATE())");

  /* Always-on suppressions (national report-template recipe); noSup = pre-suppression population */
  if (opts.noSup) C.push("email IS NOT NULL");
  else {
    C.push("email IS NOT NULL", "email <> 'none@Lennar.com'");
    ["hasoptedoutofemail", "do_not_email__c", "do_not_contact__c", "isconverted", "registration_state__c"].forEach((col) =>
      C.push("(" + col + " IS NULL OR " + col + " NOT IN ('true','True','1'))"),
    );
  }
  return C.join(" AND ");
}
